import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Trash2, ShieldCheck, RotateCcw, Package } from "lucide-react";
import SEO from "@/components/SEO";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/formatGBP";
import QtyStepper from "@/components/QtyStepper";

export default function Cart() {
  const {
    items, subtotalPennies, updateQty, removeItem, validateWithServer,
  } = useCart();

  useEffect(() => {
    if (items.length > 0) validateWithServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SEO
        title="Your basket | PeptiChill"
        description="Your PeptiChill basket. Review your selected items, update quantities, and continue to checkout."
        path="/cart"
      />
      <section className="bg-ice-light py-12 lg:py-20 min-h-[60vh]" data-testid="page-cart">
        <div className="container-pc">
          <p className="eyebrow">Basket</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-navy tracking-tightest leading-[1.05]">
            Your basket
          </h1>

          {items.length === 0 ? (
            <EmptyCart />
          ) : (
            <div className="mt-10 grid lg:grid-cols-[1.4fr,1fr] gap-10 lg:gap-14 items-start">
              <ul
                className="divide-y divide-silver/40 rounded-2xl bg-white border border-silver/40 shadow-card overflow-hidden"
                data-testid="cart-items"
              >
                {items.map((item) => (
                  <li key={item.slug} className="flex gap-5 p-5 sm:p-6">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-ice-light border border-silver/40 shrink-0">
                      {item.image && (
                        <img
                          src={item.image}
                          alt=""
                          className="w-full h-full object-cover blend-product"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-navy leading-tight">
                            {item.name}
                          </h3>
                          <p className="text-xs text-muted mt-1">
                            {formatGBP(item.price_pennies)} each
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.slug)}
                          aria-label={`Remove ${item.name}`}
                          data-testid={`cart-remove-${item.slug}`}
                          className="text-muted hover:text-destructive p-1 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                        <QtyStepper
                          value={item.quantity}
                          onChange={(q) => updateQty(item.slug, q)}
                          testIdPrefix={`cart-qty-${item.slug}`}
                        />
                        <p className="text-lg font-bold text-navy tabular-nums">
                          {formatGBP(item.price_pennies * item.quantity)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <aside className="lg:sticky lg:top-24" data-testid="cart-summary">
                <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-6">
                  <h2 className="text-lg font-bold text-navy">Order summary</h2>
                  <div className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between text-ink">
                      <span>Subtotal</span>
                      <span className="font-semibold tabular-nums">
                        {formatGBP(subtotalPennies)}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted">
                      <span>Shipping</span>
                      <span>Calculated at checkout</span>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-silver/40 flex justify-between text-navy">
                    <span className="font-semibold">Total</span>
                    <span
                      className="text-xl font-bold tabular-nums"
                      data-testid="cart-total"
                    >
                      {formatGBP(subtotalPennies)}
                    </span>
                  </div>
                  <Link
                    to="/checkout"
                    className="mt-6 w-full inline-flex items-center justify-center rounded-full bg-navy text-white h-12 text-sm font-semibold hover:brightness-110 transition shadow-card"
                    data-testid="cart-checkout-button"
                  >
                    Continue to checkout
                  </Link>
                  <p className="mt-3 text-[11px] text-muted text-center">
                    Secure payment via Stripe.
                  </p>
                </div>

                <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted justify-center">
                  <li className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-cyan" strokeWidth={2.2} />
                    Secure payment
                  </li>
                  <li className="inline-flex items-center gap-1.5">
                    <RotateCcw size={14} className="text-cyan" strokeWidth={2.2} />
                    14-day UK returns
                  </li>
                  <li className="inline-flex items-center gap-1.5">
                    <Package size={14} className="text-cyan" strokeWidth={2.2} />
                    UK tracked delivery
                  </li>
                </ul>
              </aside>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function EmptyCart() {
  return (
    <div
      className="mt-12 rounded-2xl bg-white border border-silver/40 shadow-card p-10 text-center max-w-xl"
      data-testid="cart-empty"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-ice flex items-center justify-center">
        <ShoppingBag size={22} className="text-navy" />
      </div>
      <p className="mt-5 text-navy font-semibold">Your basket is empty.</p>
      <p className="mt-2 text-sm text-muted">
        Add a PeptiChill to your basket to continue.
      </p>
      <Link
        to="/product"
        className="mt-6 inline-flex items-center justify-center rounded-full bg-navy text-white px-7 py-3 text-sm font-semibold hover:brightness-110 transition"
        data-testid="cart-empty-cta"
      >
        Shop PeptiChill
      </Link>
    </div>
  );
}
